import { ref, shallowRef } from 'vue';

import { collectPickCandidates } from '@/features/feedback/domain/pageContext';

/**
 * "Point at the thing that's wrong."
 *
 * While picking, the page keeps rendering but stops *acting*: the capture-phase
 * click handler swallows the event, so pointing at Delete does not delete
 * anything. Escape cancels.
 *
 * Three ways in, because hover is not available to everyone:
 *   - pointer: move to preview, click to pick
 *   - touch:   tap previews on `pointerdown` and picks on the click that follows
 *   - keyboard: ArrowDown / ArrowUp (or Tab) walk a candidate list, Enter picks
 *
 * Elements inside the feedback UI itself are marked `data-feedback-ui` and are
 * never candidates — you cannot report the reporter.
 */
const active = ref(false);
const hovered = shallowRef(null);

let resolvePick = null;
let candidates = [];
let candidateIndex = -1;

function isOwnUi(element) {
  return !element || element.closest?.('[data-feedback-ui]');
}

function preview(element) {
  hovered.value = isOwnUi(element) ? null : element;
}

function onPointerMove(event) {
  preview(event.target);
}

function onPointerDown(event) {
  // Touch has no hover: the tap itself is the preview. Not prevented, so the
  // click that follows still reaches the capture handler below.
  preview(event.target);
}

function onClick(event) {
  if (isOwnUi(event.target)) return;
  event.preventDefault();
  event.stopPropagation();
  finish(event.target);
}

function step(delta) {
  if (!candidates.length) candidates = collectPickCandidates();
  if (!candidates.length) return;
  candidateIndex = (candidateIndex + delta + candidates.length) % candidates.length;
  const element = candidates[candidateIndex];
  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  preview(element);
}

function onKeyDown(event) {
  if (event.key === 'Escape') {
    event.preventDefault();
    finish(null);
    return;
  }
  if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
    event.preventDefault();
    step(1);
    return;
  }
  if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
    event.preventDefault();
    step(-1);
    return;
  }
  if ((event.key === 'Enter' || event.key === ' ') && hovered.value) {
    event.preventDefault();
    finish(hovered.value);
  }
}

const LISTENERS = [
  ['pointermove', onPointerMove],
  ['pointerdown', onPointerDown],
  ['click', onClick],
  ['keydown', onKeyDown],
];

function teardown() {
  LISTENERS.forEach(([type, handler]) => window.removeEventListener(type, handler, true));
  active.value = false;
  hovered.value = null;
  candidates = [];
  candidateIndex = -1;
}

function finish(element) {
  const resolve = resolvePick;
  resolvePick = null;
  teardown();
  resolve?.(element || null);
}

/** Start picking. Resolves with the chosen element, or null when cancelled. */
function start() {
  if (active.value) return Promise.resolve(null);
  active.value = true;
  candidates = collectPickCandidates();
  candidateIndex = -1;
  // Capture phase on `window`: the app's own handlers sit on the elements, so
  // capturing is the only place we can intercept before they run.
  LISTENERS.forEach(([type, handler]) => window.addEventListener(type, handler, true));
  return new Promise((resolve) => {
    resolvePick = resolve;
  });
}

export function useElementPicker() {
  return { active, hovered, start, cancel: () => finish(null) };
}
